/**
 * Upload PDF + XML for an invoice to the private `invoice-files` bucket.
 * Path convention: {user_id}/{invoice_id}.pdf  and  {user_id}/{invoice_id}.xml
 *
 * Used after creating/editing an invoice so that the "send to accountant"
 * feature can grab the same file the user sees, without re-rendering it on
 * the server (avoids porting jsPDF + Romanian fonts to Deno).
 *
 * Best-effort: failure does NOT block the user's save flow. Logged silently.
 */
import { supabase } from "@/integrations/supabase/client";
import { generateInvoicePdf } from "./generateInvoicePdf";
import { generateInvoiceXml } from "./generateInvoiceXml";

interface InvoiceForUpload {
  id: string;
  user_id: string;
  invoice_number: string | null;
  issue_date: string;
  due_date: string | null;
  seller_name: string | null;
  seller_cui: string | null;
  seller_address: string | null;
  seller_city: string | null;
  seller_county: string | null;
  buyer_name: string | null;
  buyer_cui: string | null;
  buyer_address: string | null;
  buyer_city: string | null;
  buyer_county: string | null;
  subtotal: number;
  vat_total: number;
  total: number;
  items: {
    item_name: string;
    quantity: number;
    unit_price: number;
    vat_rate: number;
    unit_code: string;
  }[];
}

export async function uploadInvoiceFiles(invoice: InvoiceForUpload): Promise<{
  pdf_path: string | null;
  xml_path: string | null;
}> {
  const result = { pdf_path: null as string | null, xml_path: null as string | null };

  const pdfPath = `${invoice.user_id}/${invoice.id}.pdf`;
  const xmlPath = `${invoice.user_id}/${invoice.id}.xml`;

  // PDF
  try {
    const pdfDoc = generateInvoicePdf(invoice as any);
    const pdfBlob = pdfDoc.output("blob");
    const { error } = await supabase.storage
      .from("invoice-files")
      .upload(pdfPath, pdfBlob, {
        contentType: "application/pdf",
        upsert: true,
      });
    if (!error) result.pdf_path = pdfPath;
    else console.warn("PDF upload failed", error);
  } catch (err) {
    console.warn("PDF generation/upload failed", err);
  }

  // XML — only if invoice has the minimum fields needed
  try {
    const xml = generateInvoiceXml(invoice as any);
    const xmlBlob = new Blob([xml], { type: "application/xml" });
    const { error } = await supabase.storage
      .from("invoice-files")
      .upload(xmlPath, xmlBlob, {
        contentType: "application/xml",
        upsert: true,
      });
    if (!error) result.xml_path = xmlPath;
    else console.warn("XML upload failed", error);
  } catch (err) {
    // XML generation can fail validation — that's OK, drafts may be incomplete
    console.warn("XML generation/upload skipped", err);
  }

  // Persist paths on the invoice row (best-effort)
  if (result.pdf_path || result.xml_path) {
    const update: Record<string, string> = {};
    if (result.pdf_path) update.pdf_path = result.pdf_path;
    if (result.xml_path) update.xml_path = result.xml_path;
    await supabase.from("invoices").update(update as any).eq("id", invoice.id);
  }

  return result;
}

/**
 * Fetch the full invoice (with items) from DB, then upload files.
 * Convenience helper for callers that only have the invoice id.
 */
export async function uploadInvoiceFilesById(invoiceId: string): Promise<void> {
  const { data: invoice, error: invErr } = await supabase
    .from("invoices")
    .select("*")
    .eq("id", invoiceId)
    .single();
  if (invErr || !invoice) return;

  const { data: items } = await supabase
    .from("invoice_items")
    .select("*")
    .eq("invoice_id", invoiceId)
    .order("sort_order");

  await uploadInvoiceFiles({
    ...invoice,
    items: (items || []).map((i: any) => ({
      item_name: i.item_name,
      quantity: Number(i.quantity),
      unit_price: Number(i.unit_price),
      vat_rate: Number(i.vat_rate),
      unit_code: i.unit_code || "C62",
    })),
  } as InvoiceForUpload);
}
